import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import Autosuggest from "components/common/inputs/Autosuggest";
import CancelIcon from "@mui/icons-material/Cancel";
import Work from "@mui/icons-material/Work";
import { onCheckBoxChange } from "utils/propertyChangeAdapters";
import { isValid, getErrors } from "@totalsoft/pure-validations-react";
import ActivityFieldsSummary from "./ActivityFieldsSummary";
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, Checkbox, FormHelperText } from "@mui/material";
import { CollapseCard, TextField, IconButton, Button } from "@totalsoft/rocket-ui";
import CardNoResultsWrapper from "components/layout/CardNoResultsWrapper";
import { DenseHeaderCell } from "components/common/TableStyles";
import { useReactiveVar } from "@apollo/client";
import { synchronizationInProgressVar } from "variables";

const ActivityFieldsComponent = ({
  onActivityDomainsCardExpanded,
  activityDomainsCardExpanded,
  partnerId,
  saveDisabled,
  addActivityField,
  activityFields,
  onPropertyChange,
  loadActivityFieldsTypes,
  deleteActivityField,
  saveActivityFields,
  validDefaultActivitySelection,
  validActivityFieldMultipleSelection,
  validationActivityField,
  totalPercentageValid,
  partnerActivityFieldsSummary,
  hasWritePermission
}) => {
  const { t } = useTranslation();
  const synchronizationInProgress = useReactiveVar(synchronizationInProgressVar);
  const getActivityFieldValue = useCallback(item => {
    if (!item.activityFieldId) return null;
    return {
      value: item.activityFieldId,
      label:
        !item.activityFieldCode && !item.activityFieldName
          ? null
          : `${item.activityFieldCode} ${item.activityFieldName}`
    };
  }, []);

  return (
    <CollapseCard
      id="activityFieldsCard"
      title={t("Partner.ActivityDomains")}
      subheader={<ActivityFieldsSummary partnerActivityFieldsSummary={partnerActivityFieldsSummary} />}
      icon={Work}
      iconColor="primary"
      expanded={activityDomainsCardExpanded}
      onToggle={(_, expanded) => onActivityDomainsCardExpanded(!expanded)}
      defaultExpanded={synchronizationInProgress}
      hideSubheaderOnExpand
      toggleOnHeaderClick
    >
      <Grid container>
        <Grid item xs={12} sm={12} md={12}>
          <CardNoResultsWrapper content={activityFields}>
            <Table>
              <TableHead>
                <TableRow>
                  <DenseHeaderCell>{t("ActivityFields.CAENCode")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("ActivityFields.Percent")}</DenseHeaderCell>
                  <DenseHeaderCell>{t("ActivityFields.MainActivity")}</DenseHeaderCell>
                  <DenseHeaderCell></DenseHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activityFields.map((item, idx) => {
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <div id={"activityFields_" + idx + "_activityField"}>
                          <Autosuggest
                            value={getActivityFieldValue(item)}
                            onChange={onPropertyChange(idx)("activityFieldId", "activityFieldName")}
                            disabled={!hasWritePermission}
                            isClearable={false}
                            loadOptions={loadActivityFieldsTypes}
                            error={!isValid(validationActivityField[idx].activityFieldId)}
                            helperText={getErrors(validationActivityField[idx].activityFieldId)}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <TextField
                          id={"activityFields_" + idx + "_weightPercent"}
                          fullWidth
                          required
                          isNumeric
                          name="weightPercent"
                          value={item.weightPercent || ""}
                          onChange={onPropertyChange(idx)("weightPercent")}
                          disabled={!hasWritePermission}
                        />
                      </TableCell>
                      <TableCell>
                        <Checkbox
                          id={"activityFields_" + idx + "_defaultActivity"}
                          checked={item.defaultActivity || false}
                          name="defaultActivity"
                          onChange={onCheckBoxChange(onPropertyChange(idx)("defaultActivity"))}
                          disabled={!hasWritePermission}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          id={"activityFields_" + idx + "_deleteBtn"}
                          color="primary"
                          variant="text"
                          style={{ float: "right" }}
                          onClick={() => deleteActivityField(idx)}
                          disabled={!hasWritePermission}
                        >
                          <CancelIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardNoResultsWrapper>
          {!validDefaultActivitySelection && (
            <FormHelperText error={true} id="name-error-text">
              {t("ActivityFields.ValidateDefaultActivity")}
            </FormHelperText>
          )}
          {!totalPercentageValid && (
            <FormHelperText error={true} id="name-error-text">
              {t("ActivityFields.ValidateWeightPercent")}
            </FormHelperText>
          )}
          {!validActivityFieldMultipleSelection && (
            <FormHelperText error={true} id="name-error-text">
              {t("ActivityFields.ValidateActivityFieldId")}
            </FormHelperText>
          )}
          <Button
            id="activityFields_addBtn"
            color="primary"
            variant="contained"
            onClick={() => addActivityField()}
            disabled={!hasWritePermission}
            sx={{ marginTop: "1.5rem" }}
          >
            {t("Add")}
          </Button>
          <Button
            id="activityFields_saveBtn"
            color="primary"
            variant="contained"
            onClick={() => saveActivityFields(partnerId, activityFields)}
            disabled={!hasWritePermission || saveDisabled || !isValid(validationActivityField)}
            sx={{ marginLeft: ".25rem", marginTop: "1.5rem" }}
          >
            {t("Save")}
          </Button>
        </Grid>
      </Grid>
    </CollapseCard>
  );
};

ActivityFieldsComponent.propTypes = {
  partnerId: PropTypes.number,
  summary: PropTypes.array,
  onActivityDomainsCardExpanded: PropTypes.func,
  activityDomainsCardExpanded: PropTypes.bool,
  saveDisabled: PropTypes.bool,
  addActivityField: PropTypes.func.isRequired,
  loadActivityFieldsTypes: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  activityFields: PropTypes.array.isRequired,
  deleteActivityField: PropTypes.func.isRequired,
  validActivityFieldMultipleSelection: PropTypes.bool,
  saveActivityFields: PropTypes.func.isRequired,
  validDefaultActivitySelection: PropTypes.bool,
  validationActivityField: PropTypes.object,
  totalPercentageValid: PropTypes.bool,
  partnerActivityFieldsSummary: PropTypes.object,
  validActivityFieldSelection: PropTypes.object,
  onValidateActivityFieldSelection: PropTypes.func,
  hasWritePermission: PropTypes.bool.isRequired
};

export default ActivityFieldsComponent;
